import * as React from 'react'
import { AuthState, LoggedOutState, isLoggedOutState } from './authState'
import { fromJSX, Updater, Widget } from 'widgets-for-react'
import { getClassName, options } from './utils'
import {
  AuthButtonPrimary,
  FormControls,
  FormNotification,
  formSkeletonConfig,
  FormSkeletonLayout,
} from '../../utils/forms'
import { ComponentDidMount } from '../../utils/lifecycleMethods'
import { DataLayer } from '../../utils/datalayer'
import { routeUpdaters } from '../../site/root/routes/routesState'
import i18next from 'i18next'
import { Link } from 'widgets-for-react'

export function login_form(
  options: options,
  s0: LoggedOutState,
  readonly: boolean,
  displayNotification: boolean,
  studentFlow: boolean
): Widget<Updater<AuthState>> {
  const isAccountMigrated =
    s0.loggedOutState == 'logged out - must reset password'
  const notificationMessage =
    s0.loggedOutState == 'logged out - must reset password'
      ? i18next.t('auth:loginErrorMigration')
      : s0.loggedOutState == 'logged out - error login not authorized'
      ? i18next.t('auth:loginErrorNotAuthorized')
      : s0.loggedOutState === 'logged out - error account not active'
      ? i18next.t('auth:loginErrorAccountNotActive')
      : s0.loggedOutState === 'logged out - error too many attempts'
      ? i18next.t('auth:loginErrorTooManyAttempts')
      : s0.loggedOutState === "logged out - error account blocked"
      ? i18next.t('auth:loginErrorAccountBlocked')
      : i18next.t('auth:defaultErrorNotification')

  return fromJSX((setState: (_: Updater<AuthState>) => void) => (
    <FormSkeletonLayout
      type="login"
      kind="text-image"
      options={options}
      onSubmit={(e) => {
        e.preventDefault()
        setState((s) =>
          isLoggedOutState(s)
            ? {
                ...s,
                loggedOutState: 'logged out - logging in',
                login_data: s.login_data,
              }
            : s
        )
      }}
      image={formSkeletonConfig.imageLight}
    >
      <header className={getClassName(options, 'headerClassName', 'mb-4')}>
        <h1 className={getClassName(options, 'titleClassName', 'mb-4')}>
          {i18next.t('auth:loginHeader')}
        </h1>
      </header>

      {/* Error notification */}
      {displayNotification && (
        <>
          <ComponentDidMount
            onMount={() =>
              isAccountMigrated
                ? DataLayer.succesfulAccountMigration()
                : DataLayer.errorOnLoginForm(notificationMessage)
            }
          />
          <FormNotification options={options} message={notificationMessage} />
        </>
      )}

      {/* Success notification */}
      {studentFlow && (
        <FormNotification
          options={options}
          message={i18next.t(
            'studentSubscription:submitSuccessExistingStudentAccountNotification'
          )}
          status={'success'}
        />
      )}

      {/* E-mail/Username */}
      <FormControls.FormItemInGroup
        controlId="username"
        label={i18next.t('auth:email')}
        type="text"
        inputMode="email"
        readOnly={readonly}
        required={true}
        value={s0.login_data.email}
        onChange={(newValue) =>
          setState((s) =>
            isLoggedOutState(s)
              ? {
                  ...s,
                  status: 'logged out',
                  login_data: {
                    ...s.login_data,
                    email: newValue.toLowerCase(),
                  },
                }
              : s
          )
        }
        onBlur={() => DataLayer.changeLoginFormValue('emailadres')}
        validation={undefined}
        suppressHotjar={true}
      />

      {/* Password */}
      <FormControls.FormItemInGroup
        controlId="password"
        label={i18next.t('auth:password')}
        type={s0.login_data.show_password ? 'text' : 'password'}
        readOnly={readonly}
        required={true}
        value={s0.login_data.password}
        onChange={(newValue) =>
          setState((s) =>
            isLoggedOutState(s)
              ? {
                  ...s,
                  status: 'logged out',
                  login_data: {
                    ...s.login_data,
                    password: newValue,
                  },
                }
              : s
          )
        }
        validation={undefined}
        showPasswordToggle={{
          isChecked: s0.login_data.show_password,
          label: i18next.t(
            s0.login_data.show_password
              ? 'auth:hidePasswordShort'
              : 'auth:showPasswordShort'
          ),
          title: i18next.t(
            s0.login_data.show_password
              ? 'auth:hidePassword'
              : 'auth:showPassword'
          ),
          onChange: (newValue) =>
            setState((s) =>
              isLoggedOutState(s)
                ? {
                    ...s,
                    status: 'logged out',
                    login_data: {
                      ...s.login_data,
                      show_password: newValue,
                    },
                  }
                : s
            ),
        }}
        onBlur={() => DataLayer.changeLoginFormValue('wachtwoord')}
        suppressHotjar={true}
      />

      {/* Submit */}
      <div className={getClassName(options, 'formActionsClassName', '')}>
        <div className={getClassName(options, 'clickableWrapperClassName', '')}>
          <AuthButtonPrimary
            kind="button"
            type="submit"
            disabled={readonly}
            label={
              readonly ? (
                <FormControls.SubmittingSpinner />
              ) : (
                i18next.t('auth:loginSubmit')
              )
            }
            onClick={() =>
              DataLayer.clickLoginButton(i18next.t('auth:loginSubmit'))
            }
          />
        </div>
      </div>

      <footer className={getClassName(options, 'footerClassName', 'mt-4')}>
        <p className={getClassName(options, 'footerItemClassName', 'mb-1')}>
          {i18next.t('auth:loginFooterForgotPassword')}{' '}
          <Link to={routeUpdaters(i18next.language).requestResetPassword.url}>
            {i18next.t('auth:loginFooterForgotPasswordLink')}
          </Link>
        </p>
        <p className={getClassName(options, 'footerItemClassName', 'mb-1')}>
          {i18next.t('auth:loginFooterSignUp')}{' '}
          <Link
            to={
              routeUpdaters(i18next.language).register.url +
              '?originPath=' +
              window.location.pathname
            }
          >
            {i18next.t('auth:loginFooterSignUpLink')}
          </Link>
        </p>
      </footer>
    </FormSkeletonLayout>
  ))
}
